/**
 * Settings -> Payments: which methods an invoice offers.
 *
 * Two kinds of row on one list, and the panel's whole job is to keep the
 * difference visible. The two BUILT-IN rows switch real machinery - the IBAN
 * block and the SEPA code on a document, the card button in the portal - and
 * cannot be renamed or removed. Every other row is a LABEL an operator typed,
 * offered by the payment dialog and seen by nobody else. They sit on one list
 * because that is what an operator is choosing between, and each row's hint says
 * which kind it is, because a switch that looks identical to the two above it
 * would otherwise be read as one.
 *
 * Whole-form save rather than save-per-toggle, the same choice the portal
 * defaults made: each intermediate state would be a real audit row describing a
 * configuration nobody meant to ship, and a half-saved list is what an operator
 * would then have to explain.
 *
 * Order matters and is editable, because the first ENABLED row is what the
 * payment dialog prefills. That is the only thing it decides, which is why the
 * controls are two arrows rather than a drag surface.
 *
 * No permission branch in here. The tab is gated on `manage:crm.sales` in
 * `../perms.ts`, so a reader never reaches this panel - the same arrangement
 * every other panel in this section uses.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Save, Trash2, Wallet } from "lucide-react";
import {
  MAX_CUSTOM_PAYMENT_METHODS,
  MAX_PAYMENT_LABEL_LENGTH,
  type CrmPaymentMethods,
  type PaymentMethodRow,
  collidesWithReservedToken,
  customPaymentMethodId,
  defaultPaymentMethods,
  isBuiltinPaymentMethod,
  isCustomPaymentRow,
} from "@wa/core/payment-methods";
import { useT } from "../../../../i18n/I18nProvider";
import { getCrmBillingSettings } from "../../../../lib/crmBilling";
import { getPaymentMethodSettings, paymentMethodsEqual, putPaymentMethodSettings } from "../../../../lib/paymentSettings";
import { Btn, IconBtn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";

export function MethodsCard() {
  const t = useT();

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [saved, setSaved] = useState<CrmPaymentMethods>(defaultPaymentMethods);
  const [rows, setRows] = useState<PaymentMethodRow[]>(() => defaultPaymentMethods().rows);
  const [draft, setDraft] = useState("");

  /**
   * Whether Stripe itself is switched on, read from the panel next door.
   *
   * Only ever used to warn. The two switches are ANDed on the server, and the
   * honest thing for this one to do is say that the button will stay hidden -
   * not refuse the save, and not quietly flip a switch the operator did not
   * touch. `null` is "could not ask", which warns about nothing.
   */
  const [stripeOn, setStripeOn] = useState<boolean | null>(null);

  useEffect(() => {
    getPaymentMethodSettings()
      .then((data) => {
        setSaved(data);
        setRows(data.rows);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
    // A failure here is not worth a message of its own: it feeds one extra
    // warning sentence, and the panel is fully usable without it.
    getCrmBillingSettings()
      .then((s) => setStripeOn(s.enabled))
      .catch(() => setStripeOn(null));
  }, []);

  const customCount = rows.filter(isCustomPaymentRow).length;
  const full = customCount >= MAX_CUSTOM_PAYMENT_METHODS;
  const dirty = useMemo(() => !paymentMethodsEqual({ rows }, saved), [rows, saved]);
  const enabledCount = rows.filter((row) => row.enabled).length;
  const cardOn = rows.some((row) => row.id === "card" && row.enabled);
  const draftLabel = draft.trim();
  const draftTaken =
    draftLabel !== "" &&
    (collidesWithReservedToken(draftLabel) || rows.some((row) => row.id === customPaymentMethodId(draftLabel)));
  const addable = draftLabel !== "" && !draftTaken;

  function setRow(id: string, patch: Partial<PaymentMethodRow>) {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function move(index: number, by: -1 | 1) {
    setRows((prev) => {
      const target = index + by;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  function addCustom() {
    const label = draft.trim().slice(0, MAX_PAYMENT_LABEL_LENGTH);
    if (!label || full) return;
    // The server drops these on the way in, silently and correctly. Refusing
    // here as well is what makes it visible: a name that vanished on save with
    // no explanation is worse than a button that will not light up.
    if (collidesWithReservedToken(label)) return;
    const id = customPaymentMethodId(label);
    // Folded identity, the same rule the server applies on the way in. Adding a
    // name that already exists must not produce a second row that reads the same
    // and then silently loses to the first one on save.
    if (rows.some((row) => row.id === id)) {
      setDraft("");
      return;
    }
    setRows((prev) => [...prev, { id, label, enabled: true }]);
    setDraft("");
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      // What comes back is what was STORED - the server re-derives every custom
      // id from its label and drops blanks and duplicates - so the panel adopts
      // the response rather than the list it just sent.
      const next = await putPaymentMethodSettings({ rows });
      setSaved(next);
      setRows(next.rows);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  // A failed read renders NO form, and that is the important half. The state
  // behind these controls is seeded with the default list - both built-ins on,
  // no custom rows - which is a plausible-looking list that nobody chose. Left
  // editable, one toggle would enable Save and REPLACE the real row with it,
  // deleting every custom name and switching bank transfer back on across every
  // invoice. `PortalSettings` and `LabelSettings` bail out here for the same
  // reason. Reachable without a flaky request, too: the GET is `view:crm` while
  // the tab is `manage:crm.sales`, and a role holding one without the other
  // 403s here every single time.
  if (loadFailed) {
    return (
      <IntegrationCard icon={Wallet} title={t("settings.payments.title")} desc={t("settings.payments.desc")}>
        <Note tone="bad">{t("settings.payments.loadFailed")}</Note>
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      icon={Wallet}
      title={t("settings.payments.title")}
      desc={t("settings.payments.desc")}
      status={
        <ConnStatus
          ok={enabledCount > 0}
          okLabel={t("settings.payments.statusOn", { n: enabledCount })}
          offLabel={t("settings.payments.statusOff")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-6">
        <FieldGroup title={t("settings.payments.list.title")}>
          <p className="crm-sub !mt-0 text-[11.5px] leading-snug">{t("settings.payments.list.desc")}</p>
          <div className="space-y-3.5">
            {rows.map((row, index) => (
              <div key={row.id} className="flex min-w-0 items-start gap-3">
                <div className="min-w-0 flex-1">
                  <ToggleRow
                    label={
                      isBuiltinPaymentMethod(row.id)
                        ? t(`settings.payments.method.${row.id}`)
                        : (row.label ?? "")
                    }
                    hint={
                      isBuiltinPaymentMethod(row.id)
                        ? t(`settings.payments.hint.${row.id}`)
                        : t("settings.payments.hint.custom")
                    }
                    checked={row.enabled}
                    onChange={(v) => setRow(row.id, { enabled: v })}
                  />
                  {/* Renaming a custom row is deliberately NOT offered. The row
                      IS its label - that string is what gets written onto the
                      payment - so an in-place rename would look like an edit and
                      behave like a delete plus an add. Removing and adding says
                      the same thing without the disguise. */}
                </div>

                <div className="flex shrink-0 items-center gap-1 pt-0.5">
                  <IconBtn
                    small
                    aria-label={t("settings.payments.moveUp")}
                    title={t("settings.payments.moveUp")}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="size-4" />
                  </IconBtn>
                  <IconBtn
                    small
                    aria-label={t("settings.payments.moveDown")}
                    title={t("settings.payments.moveDown")}
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="size-4" />
                  </IconBtn>
                  {isCustomPaymentRow(row) ? (
                    <IconBtn
                      small
                      aria-label={t("settings.payments.remove")}
                      title={t("settings.payments.remove")}
                      onClick={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
                    >
                      <Trash2 className="size-4" />
                    </IconBtn>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </FieldGroup>

        <FieldGroup title={t("settings.payments.own.title")}>
          <p className="crm-sub !mt-0 text-[11.5px] leading-snug">{t("settings.payments.own.desc")}</p>
          {/* Enter adds the row; it does not submit the form. Without this the
              key that obviously means "add this name" would save the list
              WITHOUT it, flash "Saved", and leave the name sitting in the box -
              which reads as though it had been added. `Pill` takes no key
              handler of its own, so the listener sits on the row and catches
              the event on its way up. */}
          <div
            className="flex flex-wrap items-center gap-2"
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              addCustom();
            }}
          >
            <Pill
              value={draft}
              onChange={setDraft}
              disabled={full}
              placeholder={t("settings.payments.own.placeholder")}
              ariaLabel={t("settings.payments.own.title")}
              className="min-w-[200px] flex-1"
            />
            <Btn type="button" small tone="dark" disabled={full || !addable} onClick={addCustom}>
              <Plus className="size-4" />
              {t("settings.payments.own.add")}
            </Btn>
          </div>
          {draftTaken ? <Note tone="warn">{t("settings.payments.own.taken")}</Note> : null}
          {full ? <Note tone="mut">{t("settings.payments.own.full", { max: MAX_CUSTOM_PAYMENT_METHODS })}</Note> : null}
        </FieldGroup>

        {enabledCount === 0 ? <Note tone="bad">{t("settings.payments.emptyWarn")}</Note> : null}
        {cardOn && stripeOn === false ? <Note tone="warn">{t("settings.payments.cardNotConfigured")}</Note> : null}
        <Note tone="info">{t("settings.payments.cacheNote")}</Note>

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving" || !dirty}>
            <Save className="size-4" />
            {t("common.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("common.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("common.error")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}
